/**
 * The Guardrails page. A list of every guardrail capability, filtered by the
 * direction the guardrail runs in. Clicking a row opens its capability view.
 */
import { useMemo, useState } from "react";
import type { ProcessedClause } from "@financial-advisory/data-access/api-types";
import { Search } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { GuardrailTree, OverviewItem } from "../types";
import { buildClauseLookup, findClauseForGuardrail } from "../guardrailClauses";
import { type GuardrailRow, GuardrailsList } from "./GuardrailsList";

type DirectionFilter = "all" | "output" | "input";

interface GuardrailsSectionProps {
  guardrails: GuardrailTree[];
  clauses: ProcessedClause[];
  onOpenGraph: (item: OverviewItem) => void;
  isLoadingGuardrails: boolean;
}

export const GuardrailsSection = ({
  guardrails,
  clauses,
  onOpenGraph,
  isLoadingGuardrails,
}: GuardrailsSectionProps) => {
  const [direction, setDirection] = useState<DirectionFilter>("all");
  const [filter, setFilter] = useState("");

  const clauseLookup = useMemo(() => buildClauseLookup(clauses), [clauses]);

  const allRows: GuardrailRow[] = useMemo(
    () =>
      guardrails.map((guardrail) => ({
        guardrail,
        clause: findClauseForGuardrail(guardrail.title, clauseLookup),
      })),
    [guardrails, clauseLookup]
  );

  const outputCount = allRows.filter(
    ({ guardrail }) => guardrail.guardrailType !== "INPUT"
  ).length;
  const inputCount = allRows.length - outputCount;

  const visible = useMemo(() => {
    const query = filter.trim().toLowerCase();
    return allRows.filter(({ guardrail, clause }) => {
      const isInput = guardrail.guardrailType === "INPUT";
      if (direction === "input" && !isInput) return false;
      if (direction === "output" && isInput) return false;
      if (!query) return true;
      return (
        guardrail.title.toLowerCase().includes(query) ||
        (clause?.title.toLowerCase().includes(query) ?? false)
      );
    });
  }, [allRows, direction, filter]);

  const filters: Array<{ id: DirectionFilter; label: string }> = [
    { id: "all", label: `All ${allRows.length}` },
    { id: "output", label: `Output ${outputCount}` },
    { id: "input", label: `Input ${inputCount}` },
  ];

  return (
    <section className="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-4 px-4 py-8">
      <header className="flex flex-none items-start gap-5">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <h1 className="text-[22px] font-semibold tracking-[-0.02em]">
              Guardrails
            </h1>
            <span className="rounded-full border border-unlikely/25 bg-unlikely/[0.07] px-2.5 py-0.5 text-xs font-medium text-unlikely">
              {allRows.length}{" "}
              {allRows.length === 1 ? "capability" : "capabilities"}
            </span>
          </div>
          <p className="max-w-[720px] text-[13px] leading-relaxed text-brand-muted">
            Each guardrail is a capability that runs on a single message. Output
            guardrails run on what the assistant is about to send, input
            guardrails on what the customer sent. A reply that fails goes back to
            the agent to be rewritten.
          </p>
        </div>
        <label className="flex h-[34px] w-[210px] flex-none items-center gap-2 rounded-lg border border-brand-border bg-brand-card px-2.5 focus-within:border-unlikely/40">
          <Search
            className="h-[15px] w-[15px] flex-none text-gray-400"
            aria-hidden={true}
          />
          <input
            type="search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search guardrails"
            aria-label="Search guardrails"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-brand-foreground outline-none placeholder:text-gray-500"
          />
        </label>
      </header>

      <div className="flex flex-none items-center gap-2.5">
        <div
          className="flex gap-0.5"
          role="group"
          aria-label="Filter by direction"
        >
          {filters.map(({ id, label }) => (
            <button
              type="button"
              key={id}
              onClick={() => setDirection(id)}
              aria-pressed={direction === id}
              className={cn(
                "rounded-lg px-3 py-1 text-[13px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-unlikely/40",
                direction === id
                  ? "bg-brand-primary/15 font-semibold text-brand-foreground"
                  : "font-medium text-brand-muted hover:text-brand-foreground"
              )}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <GuardrailsList
          rows={visible}
          onOpen={({ guardrail }) => onOpenGraph(guardrail)}
          isLoading={isLoadingGuardrails}
          filter={filter}
        />
      </div>
    </section>
  );
};
